/**
 * The text-size control. One number on <html>, and the whole interface follows.
 *
 * Drop 4 rewrote every size token — --font-size-*, --space-*, --radius-*,
 * --control-*, row heights, the top bar, the sidebar — into rem, and globals.css
 * roots them at `calc(16px * var(--ui-scale, 1))`. So this module does not
 * resize anything itself. It sets one custom property and the cascade does the
 * rest, which is why this drop is small: the expensive half already shipped.
 *
 * Deliberately NOT scaled, both by design and by guard test: the content max
 * width and every media query stay in px, so turning the scale up makes the
 * text bigger inside the same column rather than pushing the layout past the
 * window; and hairlines stay at a literal 1px, because a 1.25px border renders
 * blurry and inconsistently across device pixel ratios. What makes a line feel
 * proportional is the space around it, not its thickness.
 *
 * Storage mirrors theme.ts: localStorage, per device, no migration. A
 * profiles.ui_scale column can follow later if the setting should travel
 * between machines; that is a different decision and not this one.
 */

/**
 * Three steps, and 100% is the floor.
 *
 * There was a 90% step for one drop. It worked — no overflow, alignment held,
 * contrast held at 7:1 — but measured on the live app it put column headers at
 * 9.9px and dense cell text at 10.8px, which walks back toward the small-text
 * problem this whole pass exists to fix. The control was asked for because
 * text is sometimes too small; nobody asked for a way to make it smaller
 * still, and the four extra rows it bought are not worth reopening the
 * question.
 *
 * A step under 10% is one nobody can see the effect of, so it reads as a
 * broken button — which is why the ladder does not just add finer steps
 * instead.
 *
 * Anyone whose device still remembers 0.9 lands on 100% through the clamp
 * below. No migration, nothing to clean up.
 */
export const UI_SCALES = [1, 1.1, 1.25] as const;

export type UiScale = (typeof UI_SCALES)[number];

export const DEFAULT_UI_SCALE: UiScale = 1;

export const UI_SCALE_STORAGE_KEY = 'tap-ui-scale';

/**
 * The rule, pure and testable: a saved step is honoured, anything else is 100%.
 *
 * The clamp is not defensive tidiness. `--ui-scale` multiplies the top bar and
 * the control group inside it along with everything else, so a junk value like
 * `9` from a bad write or a hand-edited localStorage would render the app at
 * 144px root — and the control you would use to undo it would be off the
 * screen. An unrecognised value has to land on 100%, or the setting can strand
 * somebody in a UI they cannot reach the fix from.
 */
export function resolveInitialScale(stored: string | null): UiScale {
  const value = Number(stored);
  return UI_SCALES.find((step) => step === value) ?? DEFAULT_UI_SCALE;
}

/** What the document is showing right now. */
export function currentScale(): UiScale {
  return resolveInitialScale(document.documentElement.style.getPropertyValue('--ui-scale'));
}

/**
 * One step along the ladder, stopping at each end.
 *
 * Clamps rather than wraps: a + button that jumps from largest back to
 * smallest is a button you cannot hold down without losing your place.
 */
export function stepScale(from: UiScale, direction: 1 | -1): UiScale {
  const index = UI_SCALES.indexOf(from);
  return UI_SCALES[index + direction] ?? from;
}

/** For the readout, and for the label a screen reader announces. */
export function percentOf(scale: UiScale): number {
  return Math.round(scale * 100);
}

/**
 * Applies the scale to the document and remembers it. Storage can be
 * unavailable (private mode); the visual change must still work.
 */
export function applyUiScale(scale: UiScale): void {
  // An inline custom property on the same element that :root declares it on.
  // Inline wins, so this needs no !important and no extra class.
  document.documentElement.style.setProperty('--ui-scale', String(scale));
  try {
    localStorage.setItem(UI_SCALE_STORAGE_KEY, String(scale));
  } catch {
    // Remembering failed; the session keeps the chosen size regardless.
  }
}
